import { Document, Link, Page, StyleSheet, Text, View } from "@react-pdf/renderer"
import { DraftWatermarkForPDF } from "components/Report/DraftWatermark"
import { ImageWithCaption, TableWithCaption } from "components/Report/ImageManager"
import { processInlineMarkdown } from "components/Report/markdown"
import { ReportFooter, ReportHeader } from "components/Report/ReportHeaderFooter"
import { Chapter, IssueStatus, Report } from "graphql/types"
import { JSX, useEffect, useState } from "react"
import { isNodeImage, isNodeTable, isNodeText } from "store/typeGuards"
import { CoverPage } from "./CoverPage"
import { documentStyles, tableStyles, typography } from "./PDFStyles"

// Rendert Inhalte eines Kapitels und fügt an oberster Stelle einen Anker ein
// Globales Register für Kapitel-Seitenzahlen (inkl. Index)
const chapterPages: Record<string, number> = {}

// Diese Komponente ermittelt die Seitenzahl und speichert sie im Register
const ChapterAnchor = ({ chapterId }: { chapterId: string }) => (
  <Text
    style={{ display: "none" }}
    render={({ pageNumber }) => {
      chapterPages[chapterId] = pageNumber
      return ""
    }}
  />
)

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 30,
    width: 595.28,
    height: 841.89,
    paddingTop: 85, // Increased from 71 to avoid header overlap
    paddingRight: 71, // 2.5cm
    paddingBottom: 85, // Increased from 57 to avoid footer overlap
    paddingLeft: 85, // 3 cm
  },
  header: {
    position: "absolute",
    top: 30, // Moved up slightly from 35
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingRight: 40,
    paddingLeft: 85,
    paddingBottom: 5, // Add some padding to the bottom of header
  },
  headerLeftText: {
    fontSize: 12,
  },
  headerRightBox: {
    fontSize: 12,
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    paddingTop: 3,
    paddingLeft: 45,
    paddingRight: 45,
    borderColor: "#000",
    fontWeight: "bold",
  },
  footer: {
    position: "absolute",
    bottom: 35, // 1,25cm
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: 71, // 2.5cm
    paddingLeft: 85, // 3 cm
    borderTopWidth: 1,
    borderTopColor: "#000",
  },
  footerTextContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  footerText: {
    fontSize: 10,
  },
  pageNumber: {
    fontSize: 10,
  },
  footerLogo: {
    width: 128,
    height: 17,
  },
  // Use professional typography styles
  title: {
    ...typography.h1,
    marginBottom: 25,
    textAlign: "center",
  },
  // Left-aligned H1 for top-level chapter headings in content
  h1Heading: {
    ...typography.h1,
    marginBottom: 12,
    marginTop: 20,
    textAlign: "left",
  },
  heading: {
    ...typography.h2,
    marginBottom: 12,
    marginTop: 20,
  },
  subheading: {
    ...typography.h3,
    marginBottom: 10,
    marginTop: 15,
  },
  text: {
    ...typography.body,
    marginBottom: 6,
  },
  toc: {
    marginBottom: 20,
  },
  tocItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 6,
    minHeight: 14,
  },
  tocDottedLine: {
    flex: 1,
    marginHorizontal: 8,
    marginBottom: 3,
    borderBottomWidth: 1,
    borderBottomColor: "#999",
    borderBottomStyle: "dotted",
  },
  image: {
    marginVertical: 10,
  },
  singleImage: {
    width: "100%",
    alignSelf: "center",
  },
  imageGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  imageGridItem: {
    width: "48%",
    marginBottom: 10,
  },
  imageCaption: {
    fontSize: 10,
    fontStyle: "italic",
    textAlign: "center",
    marginBottom: 10,
  },
  // Enhanced table styles with better borders and colors
  table: {
    ...tableStyles.table,
    pageBreakInside: "avoid",
  },
  tableRow: {
    ...tableStyles.tableRow,
  },
  tableHeader: {
    ...tableStyles.tableHeader,
  },
  tableCell: {
    ...tableStyles.tableCell,
  },
  tableHeaderCell: {
    ...tableStyles.tableHeaderCell,
  },
  tableCaption: {
    fontSize: 10,
    fontStyle: "italic",
    textAlign: "center",
    marginTop: 5,
  },
  endOfReportMarker: {
    position: "absolute",
    bottom: 80,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 10,
    fontWeight: "bold",
    color: "#003366",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
})

const getTocTextStyle = (level: number) => ({
  fontSize: level === 0 ? 10 : 9,
  color: level === 0 ? "#003366" : "#333",
  paddingLeft: level * 15, // Reduced indentation for subsections
  fontWeight: level === 0 ? "bold" : "normal",
  maxWidth: "70%",
})

const getTocPageStyle = (level: number) => ({
  fontSize: level === 0 ? 10 : 9,
  color: level === 0 ? "#003366" : "#333",
  fontWeight: level === 0 ? "bold" : "normal",
  textAlign: "right" as const,
  minWidth: 30,
})

// Generiert den TOC inklusive eines Eintrags für den Index
const generateTOC = (chapters: Chapter[], level = 0): JSX.Element[] => {
  const entries: JSX.Element[] = []

  chapters.forEach((chapter) => {
    // Skip chapters that shouldn't appear in TOC
    if (
      chapter.name === "Cover Page" ||
      chapter.name === "List of Revisions" ||
      chapter.name === "Table of Contents"
    ) {
      return
    }

    // Add main chapter entry
    entries.push(
      <View key={chapter.id} style={styles.tocItem}>
        <Text style={getTocTextStyle(level)}>
          {chapter.chapterNumber ? `${chapter.chapterNumber}. ${chapter.name}` : chapter.name}
        </Text>

        <View style={styles.tocDottedLine} />

        <Link src={`#${chapter.id}`} style={{ textDecoration: "none" }}>
          <Text style={getTocPageStyle(level)}>{chapterPages[chapter.id] || "-"}</Text>
        </Link>
      </View>
    )

    // Add subchapter entries with proper indentation
    if (chapter.subChapter && chapter.subChapter.length > 0) {
      entries.push(...generateTOC(chapter.subChapter, level + 1))
    }
  })
  return entries
}

// Inline markdown parsing is imported from shared helper

// Detects if a text node begins with a markdown heading
const getMarkdownHeadingInfo = (text: string) => {
  const firstLine = text.split("\n")[0] ?? ""
  if (firstLine.startsWith("### "))
    return { level: 3 as const, raw: firstLine, content: firstLine.slice(4) }
  if (firstLine.startsWith("## "))
    return { level: 2 as const, raw: firstLine, content: firstLine.slice(3) }
  if (firstLine.startsWith("# "))
    return { level: 1 as const, raw: firstLine, content: firstLine.slice(2) }
  return null
}

// Decide how much space we need beneath a heading to keep it with what follows
const minAheadForFollowingNode = (node: any): number => {
  if (!node) return 50
  if (isNodeImage(node)) {
    const n = (node as any)?.image?.length ?? 1
    return n === 1 ? 650 : 220
  }
  if (isNodeTable(node)) return 300
  if (isNodeText(node)) return 80 // enough to fit a few lines of text
  return 60
}

// Helper function to parse markdown-style text and apply appropriate styles
const renderMarkdownText = (text: string, nodeId: string): any => {
  const lines = text.split("\n")

  // Build tokens: heading, hr, or paragraph blocks
  const tokens: Array<{ kind: "h1" | "h2" | "h3" | "hr" | "p"; lines: string[] }> = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]

    if (line === "---") {
      tokens.push({ kind: "hr", lines: [line] })
      i++
      continue
    }

    if (line.startsWith("### ")) {
      tokens.push({ kind: "h3", lines: [line] })
      i++
      continue
    }
    if (line.startsWith("## ")) {
      tokens.push({ kind: "h2", lines: [line] })
      i++
      continue
    }
    if (line.startsWith("# ")) {
      tokens.push({ kind: "h1", lines: [line] })
      i++
      continue
    }

    // paragraph accumulator (until blank line or next control)
    if (line.trim()) {
      const p: string[] = [line]
      i++
      while (
        i < lines.length &&
        lines[i].trim() &&
        !/^#{1,3}\s/.test(lines[i]) &&
        lines[i] !== "---"
      ) {
        p.push(lines[i])
        i++
      }
      tokens.push({ kind: "p", lines: p })
      continue
    }

    // empty line -> skip
    i++
  }

  const out: JSX.Element[] = []
  for (let t = 0; t < tokens.length; t++) {
    const tok = tokens[t]

    const emitHeading = (level: 1 | 2 | 3, raw: string) => {
      const headerText = raw.replace(/^#{1,3}\s/, "")
      const processed = processInlineMarkdown(headerText)
      const style = level === 1 ? styles.title : level === 2 ? styles.heading : styles.subheading
      return (
        <Text key={`${nodeId}-h-${t}`} style={style}>
          {processed}
        </Text>
      )
    }

    if (tok.kind === "hr") {
      out.push(
        <View
          key={`${nodeId}-hr-${t}`}
          style={{ borderBottomWidth: 1, borderBottomColor: "#000", marginVertical: 10 }}
        />
      )
      continue
    }

    if (tok.kind === "h1" || tok.kind === "h2" || tok.kind === "h3") {
      // Look ahead: if next is a paragraph, group heading + that paragraph
      const next = tokens[t + 1]
      const headingLevel = tok.kind === "h1" ? 1 : tok.kind === "h2" ? 2 : 3

      if (next && next.kind === "p") {
        out.push(
          <View key={`${nodeId}-hw-${t}`} wrap={false}>
            {emitHeading(headingLevel as 1 | 2 | 3, tok.lines[0])}
            <Text key={`${nodeId}-p-${t}`} style={styles.text}>
              {processInlineMarkdown(next.lines.join("\n"))}
            </Text>
          </View>
        )
        t++ // skip the paragraph we just consumed
      } else {
        // No immediate paragraph — just emit the heading, but still add a small min-guard via a spacer
        out.push(
          <View key={`${nodeId}-honly-${t}`} wrap={false}>
            {emitHeading(headingLevel as 1 | 2 | 3, tok.lines[0])}
          </View>
        )
      }
      continue
    }

    if (tok.kind === "p") {
      out.push(
        <Text key={`${nodeId}-p-${t}`} style={styles.text}>
          {processInlineMarkdown(tok.lines.join("\n"))}
        </Text>
      )
      continue
    }
  }
  return out
}

// Determine heading style by chapter number depth (e.g., "2" -> H1, "2.1" -> H2, "2.1.1" -> H3)
const getHeadingStyleForChapter = (chapterNumber?: string) => {
  if (!chapterNumber) return styles.heading // default
  // Count numeric sections separated by dots, ignore non-numeric like "Appendix"
  const numericParts = chapterNumber.split(".").filter((p) => /^\d+$/.test(p.trim()))
  const depth = Math.max(1, numericParts.length || 1)
  if (depth === 1) return styles.h1Heading
  if (depth === 2) return styles.heading // H2
  return styles.subheading // H3+
}

const generateContent = (
  chapters: Chapter[],
  base64Images: { [key: string]: string } = {}
): any[] => {
  const content: any[] = []
  chapters.forEach((chapter) => {
    content.push(
      <View key={chapter.id} id={chapter.id}>
        {/* Seitenzahl für dieses Kapitel erfassen */}
        <ChapterAnchor chapterId={chapter.id} />
        {(() => {
          const firstNode = chapter.data?.[0]
          const minAhead = minAheadForFollowingNode(firstNode)
          return (
            <Text
              style={getHeadingStyleForChapter(chapter.chapterNumber)}
              minPresenceAhead={minAhead}
            >
              {`${chapter.chapterNumber} ${chapter.name}`.trim()}
            </Text>
          )
        })()}
        {/* Render chapter body */}
        {(() => {
          const nodes = chapter.data ?? []
          const blocks: JSX.Element[] = []
          for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i]

            if (isNodeText(node)) {
              const headingInfo = getMarkdownHeadingInfo(node.text)

              // If a text node starts with a markdown heading and there's a following block,
              // keep them together in one wrap={false} group.
              if (headingInfo && i + 1 < nodes.length) {
                const next = nodes[i + 1]
                const minAhead = minAheadForFollowingNode(next)

                const headingEl =
                  headingInfo.level === 1 ? (
                    <Text key={`${node.id}-h`} style={styles.title} minPresenceAhead={minAhead}>
                      {processInlineMarkdown(headingInfo.content)}
                    </Text>
                  ) : headingInfo.level === 2 ? (
                    <Text key={`${node.id}-h`} style={styles.heading} minPresenceAhead={minAhead}>
                      {processInlineMarkdown(headingInfo.content)}
                    </Text>
                  ) : (
                    <Text
                      key={`${node.id}-h`}
                      style={styles.subheading}
                      minPresenceAhead={minAhead}
                    >
                      {processInlineMarkdown(headingInfo.content)}
                    </Text>
                  )

                // Render the remainder of this text node (after the first heading line)
                const remaining = node.text.split("\n").slice(1).join("\n")
                const remainderEl = remaining.trim() ? (
                  <View key={`${node.id}-rest`}>
                    {renderMarkdownText(remaining, node.id + "-rest")}
                  </View>
                ) : null

                const nextEl = isNodeImage(next) ? (
                  <ImageWithCaption
                    key={(next as any).id}
                    node={next}
                    base64Images={base64Images}
                  />
                ) : isNodeTable(next) ? (
                  <TableWithCaption key={(next as any).id} node={next} />
                ) : isNodeText(next) ? (
                  <View key={(next as any).id}>
                    {renderMarkdownText((next as any).text, (next as any).id)}
                  </View>
                ) : null

                blocks.push(
                  <View key={`${node.id}-grp`} wrap={false}>
                    {headingEl}
                    {remainderEl}
                    {nextEl}
                  </View>
                )

                i++ // we consumed the next node too
                continue
              }

              // Default: just render the text node as usual
              blocks.push(<View key={node.id}>{renderMarkdownText(node.text, node.id)}</View>)
              continue
            }

            if (isNodeTable(node)) {
              // Keep tables intact: they often shouldn't split mid-rows.
              blocks.push(
                <View key={(node as any).id} wrap={false}>
                  <TableWithCaption node={node} />
                </View>
              )
              continue
            }

            if (isNodeImage(node)) {
              // Keep an image + its caption together
              blocks.push(
                <View key={(node as any).id} wrap={false}>
                  <ImageWithCaption node={node} base64Images={base64Images} />
                </View>
              )
              continue
            }

            blocks.push(null as any)
          }

          return blocks
        })()}
        {chapter.subChapter &&
          chapter.subChapter.length > 0 &&
          generateContent(chapter.subChapter, base64Images)}
      </View>
    )
  })
  return content
}

type PDFDocumentProps = {
  report: Report
  base64Images?: { [key: string]: string }
}

export const PDFDocument = (props: PDFDocumentProps) => {
  const { report, base64Images = {} } = props
  const [, forceUpdate] = useState(0)
  // Numbering is taken from provided table/image numbers; no counters used here
  // Show watermark when status is DRAFT, regardless of showDraftWatermark flag
  const showDraftWatermark = report.issueStatus === IssueStatus.DRAFT

  useEffect(() => {
    const timer = setTimeout(() => forceUpdate((prev) => prev + 1), 1000)
    return () => clearTimeout(timer)
  }, [])

  return (
    <Document>
      {report.chapter.map((item) => {
        // Handle Cover Page with professional component
        if (item.name === "Cover Page") {
          // Extract project and customer data if available from report
          const projectData = (report as any).project || {}
          return <CoverPage key={item.id} report={report} project={projectData} />
        }

        // Handle List of Revisions
        if (item.name === "List of Revisions") {
          // Derive EUT names from project EUT groups if available on report
          const projectData = (report as any).project || {}
          const eutNames = (() => {
            if (!projectData || !projectData.devices || projectData.devices.length === 0)
              return undefined
            const names: string[] = []
            projectData.devices.forEach((device: any) => {
              if (device.__typename === "DeviceEutGroup" && device.devices) {
                device.devices.forEach((eutDevice: any) => {
                  const deviceName = eutDevice?.name || eutDevice?.description
                  if (deviceName && !names.includes(deviceName)) names.push(deviceName)
                })
              }
            })
            return names.length ? names.join(", ") : undefined
          })()
          return (
            <Page key={item.id} style={[documentStyles.page, styles.page]}>
              <ReportHeader eutName={eutNames} />
              {showDraftWatermark && <DraftWatermarkForPDF show={true} />}
              <ChapterAnchor chapterId={item.id} />
              {generateContent([item], base64Images)}
              <Text
                style={styles.endOfReportMarker}
                render={({ pageNumber, totalPages }) =>
                  pageNumber === totalPages ? "END OF REPORT" : ""
                }
              />
              <ReportFooter report={report} />
            </Page>
          )
        }

        // Handle Table of Contents
        if (item.name === "Table of Contents") {
          const projectData = (report as any).project || {}
          const eutNames = (() => {
            if (!projectData || !projectData.devices || projectData.devices.length === 0)
              return undefined
            const names: string[] = []
            projectData.devices.forEach((device: any) => {
              if (device.__typename === "DeviceEutGroup" && device.devices) {
                device.devices.forEach((eutDevice: any) => {
                  const deviceName = eutDevice?.name || eutDevice?.description
                  if (deviceName && !names.includes(deviceName)) names.push(deviceName)
                })
              }
            })
            return names.length ? names.join(", ") : undefined
          })()
          return (
            <Page key={item.id} style={[documentStyles.page, styles.page]} wrap>
              <ReportHeader eutName={eutNames} />
              {showDraftWatermark && <DraftWatermarkForPDF show={true} />}
              <ChapterAnchor chapterId={item.id} />
              <Text style={[styles.title, { marginBottom: 15, color: "#003366" }]}>
                Table of Contents
              </Text>
              <View style={styles.toc}>{generateTOC(report.chapter)}</View>
              <Text
                style={styles.endOfReportMarker}
                render={({ pageNumber, totalPages }) =>
                  pageNumber === totalPages ? "END OF REPORT" : ""
                }
              />
              <ReportFooter report={report} />
            </Page>
          )
        }

        // Skip first chapter fallback if it's already been handled
        // (This was legacy code for backwards compatibility)

        // All other pages - each main chapter starts on a new page
        return (
          <Page key={item.id} style={[documentStyles.page, styles.page]} break>
            {(() => {
              const projectData = (report as any).project || {}
              const eutNames = (() => {
                if (!projectData || !projectData.devices || projectData.devices.length === 0)
                  return undefined
                const names: string[] = []
                projectData.devices.forEach((device: any) => {
                  if (device.__typename === "DeviceEutGroup" && device.devices) {
                    device.devices.forEach((eutDevice: any) => {
                      const deviceName = eutDevice?.name || eutDevice?.description
                      if (deviceName && !names.includes(deviceName)) names.push(deviceName)
                    })
                  }
                })
                return names.length ? names.join(", ") : undefined
              })()
              return <ReportHeader eutName={eutNames} />
            })()}
            {showDraftWatermark && <DraftWatermarkForPDF show={true} />}
            {generateContent([item], base64Images)}
            <Text
              style={styles.endOfReportMarker}
              render={({ pageNumber, totalPages }) =>
                pageNumber === totalPages ? "END OF REPORT" : ""
              }
            />
            <ReportFooter report={report} />
          </Page>
        )
      })}
    </Document>
  )
}
